import Video, { BandwidthProfileOptions, ConnectOptions } from 'twilio-video';
import { Room } from 'livekit-client';
import { StreamingProvider } from 'globals/enums';
import { livekitWSUrl } from 'common/components/constant';

const parseEnvNumber = (value: string | undefined, fallback: number): number => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
};

// Get the max subscription bitrate from environment variables or use default value (9Mbps)
const getMaxSubscriptionBitrate = (): number =>
  parseEnvNumber(process.env.REACT_APP_TWILIO_SUBSCRIBE_BITRATE, 9000000);

// Twilio limits the number of simultaneously subscribed video tracks to 10 by default.
// Allow this to be tuned so Windows clients with >12 active sessions can keep receiving video.
const getMaxSubscribedTracks = (): number =>
  parseEnvNumber(process.env.REACT_APP_TWILIO_MAX_SUBSCRIBED_TRACKS, 48);

const getBandwidthProfile = (): BandwidthProfileOptions => ({
  video: {
    mode: 'collaboration',
    dominantSpeakerPriority: 'standard',
    trackSwitchOffMode: 'disabled',
    maxTracks: getMaxSubscribedTracks(),
    maxSubscriptionBitrate: getMaxSubscriptionBitrate(),
    renderDimensions: {
      low: { width: 320, height: 240 },
      standard: { width: 640, height: 360 },
      high: { width: 1280, height: 720 },
    },
  },
});

const getPreferredVideoCodecs = (): ConnectOptions['preferredVideoCodecs'] => [
  { codec: 'VP8', simulcast: true },
];

const getRoom = async (stremObject: { token: string; video_provider: string }) => {
  if (stremObject.video_provider === StreamingProvider.Twilio) {
    return await Video.connect(stremObject.token, {
      audio: false,
      video: false,
      bandwidthProfile: getBandwidthProfile(),
      preferredVideoCodecs: getPreferredVideoCodecs(),
      maxAudioBitrate: parseEnvNumber(process.env.REACT_APP_TWILIO_MAX_AUDIO_BITRATE, 64000),
    });
  }
  const room = new Room();
  await room.connect(livekitWSUrl, stremObject.token);
  return room;
};

export default getRoom;
